// Hand-curated continent + business-region groupings for the country
// picker.
//
// Parallels [industry-groups.ts](industry-groups.ts): users can click
// a parent group (a continent like "Europe", or a business region like
// "EMEA") and bulk-add every member country. The chip strip shows the
// parent group name (e.g. "EMEA ×") rather than 120 individual country
// chips; the buildCrustdataFilters layer expands group names back into
// the underlying ISO-3 codes before firing the search.
//
// Why two kinds (continents AND business regions):
//   - Continents match how users think about geography.
//   - Business regions (EMEA / APAC / NAM / LATAM / MENA / ANZ) match
//     how GTM teams carve up territories. EMEA spans three continents;
//     APAC spans Asia + Oceania. Picking one is more meaningful for
//     sales work than picking three continents.
//
// Coverage: every iso3 in [countries.ts] is mapped to exactly one
// continent and zero-or-more business regions. New countries added to
// COUNTRIES will not appear in any group until added here.

import { COUNTRIES } from './countries';

export type CountryGroupKind = 'continent' | 'region';

export interface CountryGroup {
  /** Display name shown in the parent row. */
  name: string;
  /** Whether this is a geographic continent or a GTM business region. */
  kind: CountryGroupKind;
  /** One-line description shown as a subhead / tooltip. */
  description: string;
  /** Exact iso3 codes this group rolls up. */
  iso3s: string[];
}

// ── Continents ─────────────────────────────────────────────────────────

const NORTH_AMERICA = ['USA', 'CAN', 'MEX'];
const SOUTH_AMERICA = ['BRA', 'ARG', 'CHL', 'COL'];
const EUROPE = [
  'GBR',
  'DEU',
  'FRA',
  'NLD',
  'ESP',
  'ITA',
  'SWE',
  'CHE',
  'IRL',
  'BEL',
  'AUT',
  'POL',
  'DNK',
  'NOR',
  'FIN',
  'PRT',
  'CZE',
  'HUN',
  'ROU',
  'UKR',
  'GRC',
];
const ASIA = [
  'IND',
  'SGP',
  'JPN',
  'KOR',
  'CHN',
  'ISR',
  'ARE',
  'IDN',
  'MYS',
  'PHL',
  'THA',
  'VNM',
  'TWN',
  'HKG',
  'TUR',
  'SAU',
];
const AFRICA = ['ZAF', 'EGY', 'NGA', 'KEN'];
const OCEANIA = ['AUS', 'NZL'];

// ── Business regions ───────────────────────────────────────────────────
// A country can sit in multiple regions (e.g. Israel is both APAC-adj
// and EMEA depending on the team). The unions below reflect the
// conventional GTM carve-up that most B2B SaaS orgs use.

const NAM = ['USA', 'CAN'];
const LATAM = ['MEX', 'BRA', 'ARG', 'CHL', 'COL'];
const EMEA = [
  // Europe
  'GBR',
  'DEU',
  'FRA',
  'NLD',
  'ESP',
  'ITA',
  'SWE',
  'CHE',
  'IRL',
  'BEL',
  'AUT',
  'POL',
  'DNK',
  'NOR',
  'FIN',
  'PRT',
  'CZE',
  'HUN',
  'ROU',
  'UKR',
  'GRC',
  // Middle East
  'ISR',
  'ARE',
  'TUR',
  'SAU',
  // Africa
  'ZAF',
  'EGY',
  'NGA',
  'KEN',
];
const MENA = ['ISR', 'ARE', 'TUR', 'SAU', 'EGY'];
const APAC = [
  'AUS',
  'NZL',
  'IND',
  'SGP',
  'JPN',
  'KOR',
  'CHN',
  'IDN',
  'MYS',
  'PHL',
  'THA',
  'VNM',
  'TWN',
  'HKG',
];
const ANZ = ['AUS', 'NZL'];

export const COUNTRY_GROUPS: CountryGroup[] = [
  // Continents — geographic
  {
    name: 'North America',
    kind: 'continent',
    description: 'USA, Canada, Mexico',
    iso3s: NORTH_AMERICA,
  },
  {
    name: 'South America',
    kind: 'continent',
    description: 'Brazil, Argentina, Chile, Colombia',
    iso3s: SOUTH_AMERICA,
  },
  {
    name: 'Europe',
    kind: 'continent',
    description: '21 countries: UK, DACH, Nordics, Iberia, CEE',
    iso3s: EUROPE,
  },
  {
    name: 'Asia',
    kind: 'continent',
    description: '16 countries: India, China, Japan, SEA, Middle East',
    iso3s: ASIA,
  },
  {
    name: 'Africa',
    kind: 'continent',
    description: 'South Africa, Egypt, Nigeria, Kenya',
    iso3s: AFRICA,
  },
  {
    name: 'Oceania',
    kind: 'continent',
    description: 'Australia, New Zealand',
    iso3s: OCEANIA,
  },

  // Business regions — GTM territories
  {
    name: 'NAM',
    kind: 'region',
    description: 'North America: USA and Canada',
    iso3s: NAM,
  },
  {
    name: 'EMEA',
    kind: 'region',
    description: 'Europe, Middle East & Africa',
    iso3s: EMEA,
  },
  {
    name: 'APAC',
    kind: 'region',
    description: 'Asia-Pacific incl. Australia & NZ',
    iso3s: APAC,
  },
  {
    name: 'LATAM',
    kind: 'region',
    description: 'Latin America: Mexico and South America',
    iso3s: LATAM,
  },
  {
    name: 'MENA',
    kind: 'region',
    description: 'Middle East & North Africa',
    iso3s: MENA,
  },
  {
    name: 'ANZ',
    kind: 'region',
    description: 'Australia & New Zealand',
    iso3s: ANZ,
  },
];

// ── Expansion helpers ──────────────────────────────────────────────────
//
// Filter state stores group names as opaque tokens (e.g. "EMEA") in
// the same array as individual iso3 codes. buildCrustdataFilters
// passes the whole array through ``expandCountrySelection`` before
// sending it to the API.

const GROUP_BY_NAME: Map<string, CountryGroup> = new Map(
  COUNTRY_GROUPS.map((g) => [g.name, g])
);

/** Group lookup that tolerates the casing/whitespace saved state carries. */
const GROUP_BY_LOWER_NAME = new Map(
  Array.from(GROUP_BY_NAME.entries()).map(([name, group]) => [
    name.trim().toLowerCase(),
    group,
  ])
);

/** iso3 / full / display -> row, matched case-insensitively after trimming. */
const COUNTRY_BY_LOWER_TOKEN = new Map<string, (typeof COUNTRIES)[number]>();
for (const row of COUNTRIES) {
  for (const token of [row.iso3, row.full, row.display]) {
    const key = token.trim().toLowerCase();
    if (!COUNTRY_BY_LOWER_TOKEN.has(key)) COUNTRY_BY_LOWER_TOKEN.set(key, row);
  }
}

/**
 * Expand group tokens to member iso3 codes and canonicalize recognized direct
 * tokens to iso3, de-duping AFTER canonicalization — CompanyDB's ``hq_country``
 * and ``largest_headcount_country`` match iso3 only, so 'usa' / 'United States'
 * / 'United States of America' collapse to one 'USA'.
 *
 * An UNRECOGNIZED token is preserved trimmed, never dropped: dropping the only
 * value on an active control removes the predicate and BROADENS the query.
 */
export function expandCountrySelection(values: string[]): string[] {
  const out = new Set<string>();
  for (const v of values) {
    const trimmed = typeof v === 'string' ? v.trim() : v;
    if (!trimmed) continue;
    const lower = trimmed.toLowerCase();
    const group = GROUP_BY_NAME.get(trimmed) ?? GROUP_BY_LOWER_NAME.get(lower);
    if (group) {
      for (const iso3 of group.iso3s) out.add(iso3);
      continue;
    }
    out.add(COUNTRY_BY_LOWER_TOKEN.get(lower)?.iso3 ?? trimmed);
  }
  return Array.from(out);
}

/** Canonicalize one token to its full/display name, or keep it trimmed. */
function canonicalCountry(value: string, pick: 'full' | 'display'): string {
  const trimmed = value.trim();
  const row = COUNTRY_BY_LOWER_TOKEN.get(trimmed.toLowerCase());
  return row ? row[pick] : trimmed;
}

/**
 * PersonDB matches on full country names (not iso3). Resolve a mixed list of
 * country values to canonical full names.
 *
 * Accepts any form the state can carry — iso3, full name, display name or a
 * group token — because the picker, the agent-filter mapper and older saved
 * searches each write a different one.
 *
 * Regression this pins: matching iso3 ONLY silently dropped the picker's
 * full-name values, so selecting a single country applied no filter at all and
 * the search widened. Unrecognized values are preserved, not dropped, for the
 * same reason — see ``expandCountrySelection``.
 */
export function expandCountrySelectionToFullNames(values: string[]): string[] {
  // Dedupe AFTER canonicalization: "USA" and "United States" are one value.
  return Array.from(
    new Set(
      expandCountrySelection(values).map((v) => canonicalCountry(v, 'full'))
    )
  );
}

/**
 * Resolve a mixed list of country values to canonical LinkedIn-style DISPLAY
 * names ("United States", "United Kingdom").
 *
 * Use this for the person ``region`` and ``company_hq_location`` filters, which
 * match LinkedIn-derived location STRINGS carrying the short country name.
 * Resolving to the official ``full`` name there matches ZERO rows — the US and
 * UK are the only two countries where full != display, so every other country
 * is unaffected.
 */
export function expandCountrySelectionToDisplayNames(
  values: string[]
): string[] {
  // Dedupe AFTER canonicalization: "USA" and "United States" are one value.
  return Array.from(
    new Set(
      expandCountrySelection(values).map((v) => canonicalCountry(v, 'display'))
    )
  );
}

// ── Picker adapters ────────────────────────────────────────────────────
//
// FilterPicker.grouped-list mode wants ``PickerGroup[]`` where each
// child has a wire value matching the surrounding state shape:
//   - PersonDB current_employers.company_headquarters_country expects
//     FULL names ("United States of America", "Germany")
//   - CompanyDB hq_country expects ISO-3 ("USA", "DEU")
// Both helpers below sit on top of the canonical iso3-keyed groups
// and project each child into the requested format.

interface PickerChild {
  value: string;
  label: string;
}

export interface PickerGroupForFilter {
  name: string;
  description: string;
  /** The heading the picker shows over this kind of group. */
  section: string;
  children: PickerChild[];
}

function projectGroup(
  group: CountryGroup,
  format: 'iso3' | 'full'
): PickerGroupForFilter {
  const children: PickerChild[] = [];
  for (const iso3 of group.iso3s) {
    const row = COUNTRIES.find((c) => c.iso3 === iso3);
    if (!row) continue;
    children.push({
      value: format === 'iso3' ? row.iso3 : row.full,
      label: row.display,
    });
  }
  return {
    name: group.name,
    description: group.description,
    section: group.kind === 'continent' ? 'Continents' : 'Business regions',
    children,
  };
}

/** Groups projected for a picker bound to ISO-3 state (Company HQ). */
export function getCountryGroupsIso3(): PickerGroupForFilter[] {
  return COUNTRY_GROUPS.map((g) => projectGroup(g, 'iso3'));
}

/** Groups projected for a picker bound to full-name state (People
 *  contact / HQ on the person side). */
export function getCountryGroupsFull(): PickerGroupForFilter[] {
  return COUNTRY_GROUPS.map((g) => projectGroup(g, 'full'));
}

/** All group-token names (for FilterPicker ``groupTokens`` prop so it
 *  knows which array entries to render as group chips). */
export const COUNTRY_GROUP_NAMES: string[] = COUNTRY_GROUPS.map((g) => g.name);
